import { LoadingIndicator } from "../LoadingIndicator/index.js";
import "./ag-theme-custom.css";
import { AG_GRID_LOCALE_RU } from "./ruLocale.js";
import { TablePagination } from "./TablePagination.js";
import { getServerSidePaginationGridOptions, isServerSidePagination, resolvePageSizeSelector, toGridPage, toUiPage } from "./TablePagination.lib.js";
import React from "react";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { AllCommunityModule, ModuleRegistry, provideGlobalGridOptions } from "ag-grid-community";
import { AllEnterpriseModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
//#region src/TableAg/index.tsx
const originalError = console.error;
console.error = (...args) => {
	if (args[0] && typeof args[0] === "string" && (args[0].includes("*****") || args[0].includes("All AG Grid Enterprise") || args[0].includes("If you want to hide the watermark"))) return;
	originalError.apply(console, args);
};
ModuleRegistry.registerModules([AllEnterpriseModule, AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });
const LoadingIndicatorOverlay = () => /* @__PURE__ */ jsx(LoadingIndicator, {});
const Table = ({ tableColumns, tableData, getContextMenuItems, onSelectRow, isLoading, pagination = false, paginationPageSize = 10, paginationPageSizeSelector, onPaginationChanged, getDataPath, selectedRows, overlayNoRowsTemplate, isSortable = true, hasFilters = true, suppressHeaderMenuButton = false, module = "default", suppressHeaderContextMenu = false, selectionType = "none", tableId = "default-table", className: userClassName = "", onGridReady, onCellContextMenu, ...props }) => {
	const gridRef = React.useRef(null);
	const localStorageKey = `table-settings-${tableId}`;
	const pageSizeSelector = resolvePageSizeSelector(paginationPageSizeSelector);
	const [paginationState, setPaginationState] = React.useState(() => ({
		currentPage: 1,
		pageSize: paginationPageSize,
		totalItems: tableData?.length ?? 0
	}));
	const [initialState] = React.useState(() => {
		const raw = localStorage.getItem(localStorageKey);
		if (!raw) return null;
		try {
			return JSON.parse(raw);
		} catch {
			return null;
		}
	});
	const rowSelection = React.useMemo(() => ({
		mode: "multiRow",
		enableClickSelection: true
	}), []);
	const rowSingleSelection = React.useMemo(() => ({
		mode: "singleRow",
		checkboxes: true,
		checkboxLocation: "autoGroupColumn",
		enableClickSelection: true
	}), []);
	const selectionTypeOptions = React.useMemo(() => {
		if (selectionType === "none") return;
		return selectionType === "single" ? rowSingleSelection : rowSelection;
	}, [
		rowSelection,
		rowSingleSelection,
		selectionType
	]);
	const defaultColDef = React.useMemo(() => ({
		sortable: isSortable,
		filter: hasFilters,
		suppressHeaderMenuButton,
		suppressHeaderContextMenu
	}), [
		hasFilters,
		isSortable,
		suppressHeaderContextMenu,
		suppressHeaderMenuButton
	]);
	const selectionColumnDef = React.useMemo(() => ({
		width: 36,
		maxWidth: 36,
		pinned: true
	}), []);
	const moduleThemeMap = {
		ekptd: "ag-theme-quartz ekptd-theme",
		gge: "ag-theme-quartz gge-theme",
		default: "ag-theme-quartz asu-theme"
	};
	const themeClassName = moduleThemeMap[module] ?? moduleThemeMap.default;
	const handleSelectionChanged = React.useCallback((event) => {
		onSelectRow?.(event.api.getSelectedRows());
	}, [onSelectRow]);
	const onStateUpdated = React.useCallback((params) => {
		localStorage.setItem(localStorageKey, JSON.stringify(params.state));
	}, [localStorageKey]);
	const onGridPreDestroyed = React.useCallback((params) => {
		localStorage.setItem(localStorageKey, JSON.stringify(params.state));
	}, [localStorageKey]);
	const syncPaginationState = React.useCallback((api) => {
		if (api.isDestroyed()) return;
		setPaginationState((prev) => {
			const next = {
				currentPage: toUiPage(api.paginationGetCurrentPage()),
				pageSize: api.paginationGetPageSize(),
				totalItems: api.paginationGetRowCount()
			};
			if (prev.currentPage === next.currentPage && prev.pageSize === next.pageSize && prev.totalItems === next.totalItems) return prev;
			return next;
		});
	}, []);
	const handleGridReady = React.useCallback((params) => {
		if (pagination) syncPaginationState(params.api);
		onGridReady?.(params);
	}, [
		onGridReady,
		pagination,
		syncPaginationState
	]);
	const handlePaginationChanged = React.useCallback((event) => {
		syncPaginationState(event.api);
		onPaginationChanged?.(event);
	}, [onPaginationChanged, syncPaginationState]);
	const handlePageChange = React.useCallback((page) => {
		gridRef.current?.api?.paginationGoToPage(toGridPage(page));
	}, []);
	const handlePageSizeChange = React.useCallback((pageSize) => {
		const api = gridRef.current?.api;
		if (!api) return;
		api.setGridOption("paginationPageSize", pageSize);
		if (api.getGridOption("rowModelType") === "serverSide") api.setGridOption("cacheBlockSize", pageSize);
	}, []);
	const handleCellContextMenu = React.useCallback((event) => {
		if (!event.node.isSelected()) {
			event.api.deselectAll();
			event.node.setSelected(true);
		}
		onCellContextMenu?.(event);
	}, [onCellContextMenu]);
	React.useEffect(() => {
		gridRef.current?.api?.forEachNode((node) => {
			const rowId = node.data?.id;
			node.setSelected(selectedRows?.includes(rowId ?? "") ?? false);
		});
	}, [selectedRows]);
	const gridClassName = pagination ? cn(themeClassName, "min-h-0 min-w-0 flex-1") : cn(userClassName, themeClassName);
	const serverSidePagination = isServerSidePagination(pagination, props.rowModelType);
	const gridProps = {
		animateRows: false,
		onGridReady: handleGridReady,
		localeText: AG_GRID_LOCALE_RU,
		loading: isLoading,
		ref: gridRef,
		rowData: tableData,
		columnDefs: tableColumns,
		rowSelection: selectionTypeOptions,
		onSelectionChanged: onSelectRow ? handleSelectionChanged : void 0,
		suppressContextMenu: getContextMenuItems === void 0,
		defaultColDef,
		getContextMenuItems,
		selectionColumnDef,
		suppressServerSideFullWidthLoadingRow: true,
		loadingOverlayComponent: props.loadingOverlayComponent ?? LoadingIndicatorOverlay,
		overlayNoRowsTemplate,
		treeData: getDataPath !== void 0,
		getDataPath,
		onStateUpdated,
		onGridPreDestroyed,
		initialState: initialState ?? void 0,
		onCellContextMenu: handleCellContextMenu,
		...props,
		popupParent: props.popupParent ?? (typeof document === "undefined" ? void 0 : document.body),
		pagination,
		paginationPageSize: paginationState.pageSize,
		suppressPaginationPanel: pagination,
		onPaginationChanged: pagination ? handlePaginationChanged : onPaginationChanged,
		...serverSidePagination ? getServerSidePaginationGridOptions(paginationState.pageSize) : {},
		className: gridClassName
	};
	const grid = /* @__PURE__ */ jsx(AgGridReact, { ...gridProps });
	if (!pagination) return grid;
	return /* @__PURE__ */ jsxs("div", {
		className: cn(userClassName, "flex h-full min-h-0 w-full flex-col"),
		children: [grid, /* @__PURE__ */ jsx(TablePagination, {
			totalItems: paginationState.totalItems,
			pageSize: paginationState.pageSize,
			currentPage: paginationState.currentPage,
			pageSizeSelector,
			onPageChange: handlePageChange,
			onPageSizeChange: handlePageSizeChange
		})]
	});
};
Table.displayName = "Table";
//#endregion
export { Table };

//# sourceMappingURL=index.js.map